import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Domain API Helper für Firebase Hosting API Integration
 */

const functions = getFunctions();

/**
 * Fügt Custom Domain über Firebase Hosting API hinzu
 */
export const addCustomDomain = async (domain, userId) => {
  try {
    console.log('🌐 Adding custom domain via API:', { domain, userId });
    
    const addDomainFunction = httpsCallable(functions, 'addCustomDomain');
    const result = await addDomainFunction({ domain, userId });
    
    console.log('✅ Domain added successfully:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('❌ Error adding custom domain:', error);
    throw new Error(error.message || 'Failed to add custom domain');
  }
};

/**
 * Prüft Domain-Status über Firebase Hosting API
 */
export const checkDomainStatus = async (domain, userId) => {
  try {
    console.log('🔍 Checking domain status:', { domain, userId });
    
    const checkStatusFunction = httpsCallable(functions, 'checkDomainStatus');
    const result = await checkStatusFunction({ domain, userId });
    
    console.log('✅ Domain status checked:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('❌ Error checking domain status:', error);
    throw new Error(error.message || 'Failed to check domain status');
  }
};

/**
 * Entfernt Custom Domain über Firebase Hosting API
 */
export const removeCustomDomain = async (domain, userId) => {
  try {
    console.log('🗑️ Removing custom domain:', { domain, userId });
    
    const removeDomainFunction = httpsCallable(functions, 'removeCustomDomain');
    const result = await removeDomainFunction({ domain, userId });
    
    console.log('✅ Domain removed successfully:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('❌ Error removing custom domain:', error);
    throw new Error(error.message || 'Failed to remove custom domain');
  }
};

/**
 * Polling Helper für Domain-Status
 */
export class DomainStatusPoller {
  constructor(domain, userId, onStatusChange) {
    this.domain = domain;
    this.userId = userId;
    this.onStatusChange = onStatusChange;
    this.intervalId = null;
    this.isPolling = false;
  }

  start(intervalMs = 30000) { // 30 seconds default
    if (this.isPolling) return;
    
    this.isPolling = true;
    console.log(`🔄 Starting domain status polling for ${this.domain}`);
    
    // Initial check
    this.checkStatus();
    
    // Set up polling
    this.intervalId = setInterval(() => {
      this.checkStatus();
    }, intervalMs);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isPolling = false;
    console.log(`⏹️ Stopped domain status polling for ${this.domain}`);
  }

  async checkStatus() {
    try {
      const result = await checkDomainStatus(this.domain, this.userId);
      
      if (this.onStatusChange) {
        this.onStatusChange(result);
      }

      // Stop polling if domain is active or has error
      if (result.isActive || result.status === 'error') {
        this.stop();
      }

    } catch (error) {
      console.error('❌ Polling error:', error);
      
      if (this.onStatusChange) {
        this.onStatusChange({
          success: false,
          error: error.message,
          status: 'error'
        });
      }
      
      this.stop();
    }
  }
}

/**
 * DNS Record Helper Functions
 */
export const copyDNSRecord = (record) => {
  const text = `${record.type} ${record.name} ${record.value}`;
  
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(() => {
      console.log('✅ DNS record copied to clipboard');
    }).catch(err => {
      console.error('❌ Failed to copy:', err);
    });
  } else {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    console.log('✅ DNS record copied (fallback method)');
  }
};

/**
 * Validiert Domain-Format für Firebase Hosting
 */
export const validateDomainForHosting = (domain) => {
  // Remove protocol if present
  const cleanDomain = domain.replace(/^https?:\/\//, '').toLowerCase();
  
  // Basic validation
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.([a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})$/;
  
  if (!domainRegex.test(cleanDomain)) {
    return { valid: false, error: 'Invalid domain format' };
  }
  
  // Check for reserved domains
  const reservedDomains = ['bonusclub.link', 'firebase.app', 'firebaseapp.com', 'web.app'];
  if (reservedDomains.some(reserved => cleanDomain.includes(reserved))) {
    return { valid: false, error: 'Reserved domain' };
  }
  
  // Check length
  if (cleanDomain.length > 253) {
    return { valid: false, error: 'Domain too long' };
  }
  
  return { valid: true, domain: cleanDomain };
};

/**
 * Generiert DNS-Setup Anleitung für verschiedene Provider
 */
export const getDNSInstructions = (provider, records) => {
  const instructions = {
    godaddy: {
      name: 'GoDaddy',
      steps: [
        'Sign in to your GoDaddy account',
        'Go to your product page',
        'Under "Web Hosting," next to the Linux Hosting account you want to use, click "Manage"',
        'In the account Dashboard, click "DNS Zone File"',
        'Click "Edit Zone File"',
        'Add the DNS records shown below'
      ]
    },
    namecheap: {
      name: 'Namecheap',
      steps: [
        'Sign in to your Namecheap account',
        'Navigate to Domain List on the left sidebar',
        'Click the "Manage" button next to your domain',
        'Navigate to the "Advanced DNS" tab',
        'Add the DNS records shown below'
      ]
    },
    cloudflare: {
      name: 'Cloudflare',
      steps: [
        'Sign in to your Cloudflare account',
        'Click on your domain',
        'Navigate to the "DNS" tab',
        'Add the DNS records shown below',
        'Make sure proxy status is set to "DNS only" (gray cloud)'
      ]
    }
  };

  return instructions[provider] || {
    name: 'Your DNS Provider',
    steps: [
      'Sign in to your DNS provider',
      'Navigate to DNS management',
      'Add the DNS records shown below',
      'Save your changes'
    ]
  };
};
